import clsx from 'clsx';
import styles from 'components/UI/V3/MediaGalleryGrid/Item/MediaGalleryGirdItem.module.scss';
import React from 'react';

export interface MediaItem {
    url: string;
    type: 'image' | 'video';
}

interface MediaGalleryGridItemProps {
    item: MediaItem;
    showOverlay?: boolean;
    overlayText?: string;
    onClick?: (_item: MediaItem) => void;
}

const VideoWithSkeleton: React.FC<{ src: string; className: string }> = ({ src, className }) => {
    const [isLoading, setIsLoading] = React.useState(true);
    const [thumbnail, setThumbnail] = React.useState<string | undefined>(undefined);

    React.useEffect(() => {
        const video = document.createElement('video');
        video.muted = true;
        video.autoplay = true;
        video.playsInline = true;
        video.preload = 'metadata';
        video.crossOrigin = 'anonymous';
        video.src = src;

        const onLoadedData = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                setIsLoading(false);
                return;
            }

            try {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;

                ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

                const dataURL = canvas.toDataURL('image/png');
                setThumbnail(dataURL);
            } finally {
                setIsLoading(false);
            }
        };

        const onError = () => {
            setIsLoading(false);
        };

        video.addEventListener('loadeddata', onLoadedData);
        video.addEventListener('error', onError);
        video.load();

        return () => {
            video.removeEventListener('loadeddata', onLoadedData);
            video.removeEventListener('error', onError);
            video.src = '';
        };
    }, [src]);

    return (
        <>
            {isLoading && <div className={clsx(className, styles.skeleton)} />}

            {thumbnail && (
                <img
                    src={thumbnail}
                    alt="Video thumbnail"
                    className={clsx(className, { [styles.hidden]: isLoading })}
                />
            )}
        </>
    );
};

const ImageWithSkeleton: React.FC<{ src: string; alt: string; className: string }> = ({ src, alt, className }) => {
    const [isLoading, setIsLoading] = React.useState(true);

    const handleLoad = React.useCallback(() => {
        setIsLoading(false);
    }, []);

    const handleError = React.useCallback(() => {
        setIsLoading(false);
    }, []);

    return (
        <>
            {isLoading && <div className={clsx(className, styles.skeleton)} />}

            <img
                src={src}
                alt={alt}
                className={clsx(className, { [styles.hidden]: isLoading })}
                onLoad={handleLoad}
                onError={handleError}
            />
        </>
    );
};

export const MediaGalleryGridItem: React.FC<MediaGalleryGridItemProps> = ({
    item,
    showOverlay,
    overlayText,
    onClick
}) => {
    return (
        <div
            className={styles.root}
            onClick={() => onClick?.(item)}
        >
            {item.type === 'video' ? (
                <VideoWithSkeleton
                    src={item.url}
                    className={styles.media}
                />
            ) : (
                <ImageWithSkeleton
                    src={item.url}
                    alt="Gallery item"
                    className={styles.media}
                />
            )}

            {showOverlay && (
                <div className={styles.overlay}>
                    <span>{overlayText}</span>
                </div>
            )}
        </div>
    );
};
